import {
  InputStartAdornment,
  SelectV4,
  TextInputV4,
} from '@labrav/react-components'
import { Grid, SelectChangeEvent, Typography } from '@mui/material'
import { cloneDeep, get } from 'lodash'
import {
  AzureOfferPlan,
  AzurePriceInputOption,
  AzurePricingType,
  AzureRecurrentPriceField,
  BillingTerm,
  CustomMeters,
  Installments,
  OfferRecurrentPricingType,
  RecurrentPriceModeType,
  ScheduleType,
} from '../../../../../modules/privateOffer/reducer'
import {
  AzurePlanDimension,
  Product,
  ProductPlan,
} from '../../../../../modules/productsListing/reducer'
import { FormikProps, useFormikContext } from 'formik'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import { useStyles } from '../PlanRow.styles'
import { isNonEmptyNumber } from '../../../../../../common/utils/validateMinMax'
import { DisplayValueData } from '../MeteringDimensions/MeteringDimensions'
import { getDiscountedPriceValue } from '../../../../../../common/utils/getDiscountedPriceValue'
import {
  getInstallmentsBasedOnSchedule,
  getPriceTitleAndValue,
} from '../../../../../utils/resaleAuthorizationUtil'
import { getDurationValueFromBillingTerm } from '../PlanRowComponent'
import { useCallback } from 'react'
import { getUpdatedCustomMeters } from '../PlanRow'

export const ContractSelection = ({
  plan,
  product,
  index,
  selectedPaymentOptionValue,
}: {
  plan: AzureOfferPlan
  index: number
  product: Product
  selectedPaymentOptionValue: string
}) => {
  const { classes } = useStyles()
  const { values, touched, errors, handleChange, handleBlur, setFieldValue } =
    useFormikContext() as FormikProps<Record<string, unknown>>

  const productPlan = product?.plans?.find(
    planItem => planItem.planId === plan?.labraPlanId
  )

  const azurePricingType = values?.azurePricingType as AzurePricingType

  const isDiscountPricing =
    azurePricingType === AzurePricingType.LABRA_PERCENTAGE

  const planBillings = productPlan
    ? convertProductPlanToOfferPlan(
        productPlan
      )?.originalPricing?.recurrentPrice?.prices?.map(item =>
        getBillingTermsList(item?.billingTerm)
      )
    : []

  const handleContractDurationChange = useCallback(
    (e: SelectChangeEvent) => {
      const { value } = e.target
      if (productPlan) {
        const selectedOption = planBillings?.find(item => item.value === value)
        const billingTerm = {
          type: selectedOption?.type || '',
          value: selectedOption?.termValue || 0,
        }

        if (billingTerm) {
          const prices =
            convertProductPlanToOfferPlan(
              productPlan
            )?.originalPricing?.recurrentPrice?.prices?.filter(
              item =>
                item?.billingTerm?.type === billingTerm?.type &&
                item?.billingTerm?.value === billingTerm?.value
            ) || []

          const customMeters = getCustomMeteres({
            productPlan,
            pricesValue: prices,
          })

          const updatedCustomMeters = getUpdatedCustomMeters(
            plan,
            plan?.discountPercentage,
            { meters: customMeters } as CustomMeters
          )

          const updatedPrices = prices[0]
          const mppoDuration = getDurationValueFromBillingTerm(billingTerm)
          const installments = getInstallmentsBasedOnSchedule({
            tcv: getDiscountedPriceValue({
              price: prices[0].pricePerPaymentInUsd || 0,
              discount: plan?.discountPercentage,
            }),
            durationInMonths: mppoDuration || 0,
            paymentSchedule: ScheduleType.MONTHLY,
            now: (values?.offerStartDate as string) || '',
          }) as unknown as Installments[]

          const updatePlanRow = {
            ...plan,
            pricing: {
              ...plan.pricing,
              recurrentPrice: {
                ...plan.pricing.recurrentPrice,
                prices: [
                  {
                    ...updatedPrices,
                    ...(selectedPaymentOptionValue === 'flexiblePayment' &&
                    mppoDuration > 1
                      ? {
                          installments: {
                            frequency: ScheduleType.MONTHLY,
                            tcv: getDiscountedPriceValue({
                              price: prices[0].pricePerPaymentInUsd || 0,
                              discount: plan?.discountPercentage,
                            }),
                            schedule: installments,
                          },
                          pricePerPaymentInUsd: undefined,
                        }
                      : {
                          installments: undefined,
                          pricePerPaymentInUsd: getDiscountedPriceValue({
                            price: prices[0].pricePerPaymentInUsd || 0,
                            discount: plan?.discountPercentage,
                          }),
                        }),
                  },
                ],
              },
              customMeters: {
                priceInputOption: AzurePriceInputOption.USD,
                ...plan.pricing.customMeters,
                ...updatedCustomMeters,
              },
            },
          }
          const plans = [...(values.plans as AzureOfferPlan[])]
          plans[index] = {
            ...cloneDeep(updatePlanRow),
          } as AzureOfferPlan
          setFieldValue('plans', [...cloneDeep(plans)])
        }
      }
    },
    [productPlan, planBillings, selectedPaymentOptionValue, index, values]
  )

  const billingTermItem = {
    ...cloneDeep(plan.pricing.recurrentPrice.prices[0].billingTerm),
  }

  const { title: tcvTitle } = getPriceTitleAndValue({
    azurePricingType,
    plan,
    recurrentPriceMode: plan.pricing.recurrentPrice.recurrentPriceMode,
  })

  const isFlexiblePayment = selectedPaymentOptionValue === 'flexiblePayment'
  const tcvValue = isFlexiblePayment
    ? plan.pricing.recurrentPrice.prices[0].installments?.tcv
    : plan.pricing.recurrentPrice.prices[0].pricePerPaymentInUsd

  const tcvKeyName = isFlexiblePayment
    ? `plans.${[index]}.pricing.recurrentPrice.prices.0.installments.tcv`
    : `plans.${[index]}.pricing.recurrentPrice.prices.0.pricePerPaymentInUsd`

  const handleTcvChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      const numericValue = value !== '' ? Number(value) : undefined

      if (isFlexiblePayment && numericValue !== undefined) {
        const mppoDuration = getDurationValueFromBillingTerm(billingTermItem)
        const installments = getInstallmentsBasedOnSchedule({
          tcv: numericValue,
          durationInMonths: mppoDuration || 0,
          paymentSchedule:
            plan.pricing.recurrentPrice.prices[0].installments?.frequency ||
            ScheduleType.MONTHLY,
          now: (values?.offerStartDate as string) || '',
        }) as unknown as Installments[]

        const updatedPlan = {
          ...plan,
          pricing: {
            ...plan.pricing,
            recurrentPrice: {
              ...plan.pricing.recurrentPrice,
              prices: [
                {
                  ...plan.pricing.recurrentPrice.prices[0],
                  installments: {
                    ...plan.pricing.recurrentPrice.prices[0].installments,
                    tcv: numericValue,
                    schedule: installments,
                  },
                },
              ],
            },
          },
        }

        const plans = [...(values.plans as AzureOfferPlan[])]
        plans[index] = cloneDeep(updatedPlan)
        setFieldValue('plans', [...cloneDeep(plans)])
      } else {
        handleChange(event)
      }
    },
    [isFlexiblePayment, billingTermItem, plan, values, index, setFieldValue]
  )

  return (
    <Grid container width={'100%'} spacing={3}>
      <Grid size={6} width={'248px'}>
        <SelectV4
          field={{
            title: 'Contract duration',
            placeHolder: 'Contract duration',
            humanReadableKey: 'plan',
            isRequired: false,
            isReadOnly: false,
            isCustom: false,
            defaultValue: '',
            dataType: 'enum' as DataType,
            options: planBillings,
          }}
          size="small"
          value={
            `${billingTermItem?.value}${' '}${billingTermItem?.type}` || ''
          }
          onChange={handleContractDurationChange}
          onBlur={handleBlur}
          isError={
            !!(
              get(
                touched,
                `plans.${[index]}.pricing.recurrentPrice.prices[0].billingTerm.value`,
                false
              ) &&
              get(
                errors,
                `plans.${[index]}.pricing.recurrentPrice.prices[0].billingTerm.value`,
                false
              )
            )
          }
          errorText={
            (!!get(
              touched,
              `plans.${[index]}.pricing.recurrentPrice.prices[0].billingTerm.value`,
              false
            ) &&
              get(
                errors,
                `plans.${[index]}.pricing.recurrentPrice.prices[0].billingTerm.value`,
                ''
              )) ||
            ''
          }
        />
      </Grid>
      <Grid size={6}>
        <div className={classes.tcv}>
          {!isDiscountPricing ? (
            <TextInputV4
              keyName={tcvKeyName}
              title={tcvTitle}
              type="number"
              placeHolder=""
              keyToPrevent={['+', '-', 'e']}
              value={isNonEmptyNumber(tcvValue) ? tcvValue! : ''}
              updatedValue={isNonEmptyNumber(tcvValue) ? tcvValue! : ''}
              startAdornment={<InputStartAdornment text="$" />}
              alignRight
              isError={
                !!(
                  get(touched, tcvKeyName, false) &&
                  get(errors, tcvKeyName, false)
                )
              }
              onChange={handleTcvChange}
              isReadOnly={false}
              isRequired={false}
              errorText={
                (!!get(touched, tcvKeyName, false) &&
                  get(errors, tcvKeyName, '')) ||
                ''
              }
              showSpinner={false}
              onBlur={handleBlur}
            />
          ) : (
            <div className={classes.discountedTcv}>
              <div className={classes.title}>{tcvTitle}</div>
              <DisplayValueData id={tcvKeyName} value={tcvValue} isPrice />
            </div>
          )}
        </div>
      </Grid>
    </Grid>
  )
}

export const convertProductPlanToOfferPlan = (productPlan?: ProductPlan) => {
  if (!productPlan) return productPlan

  const { pricingData, originalPricingData } =
    convertProductPlanPricingToOffer(productPlan)

  const offerPlan = {
    planId: productPlan?.planId,
    labraPlanId: productPlan.planId,
    azurePlanId: productPlan?.azurePlanId,
    description: productPlan?.description,
    displayName: productPlan?.displayName,
    offerPricingType: OfferRecurrentPricingType.SAASNEWCUSTOMIZEDPLANS,
    pricing: pricingData,
    originalPricing: originalPricingData,
    discountPercentage: null,
  }
  return { ...offerPlan }
}

export const convertProductPlanPricingToOffer = (productPlan: ProductPlan) => {
  const pricesValue = [
    get(productPlan, 'pricing', [])[0],
  ] as AzureRecurrentPriceField[]

  const recurrentPriceModeValue =
    isNonEmptyNumber(productPlan?.minQuantity) &&
    isNonEmptyNumber(productPlan?.maxQuantity)
      ? RecurrentPriceModeType.PERUSER
      : RecurrentPriceModeType.FLATRATE
  const meters = { ...getCustomMeteres({ productPlan, pricesValue }) }

  return {
    pricingData: {
      recurrentPrice: {
        recurrentPriceMode: recurrentPriceModeValue,
        priceInputOption: AzurePriceInputOption.USD,
        userLimits: {
          min: productPlan?.minQuantity,
          max: productPlan?.maxQuantity,
          minEnable: true,
          maxEnable: true,
        },
        prices: pricesValue,
      },
      customMeters: {
        meters: { ...meters },
      },
    },
    originalPricingData: {
      recurrentPrice: {
        recurrentPriceMode: recurrentPriceModeValue,
        priceInputOption: AzurePriceInputOption.USD,
        userLimits: {
          min: productPlan?.minQuantity,
          max: productPlan?.maxQuantity,
          minEnable: true,
          maxEnable: true,
        },
        prices: productPlan?.pricing,
      },
      customMeters: {
        meters: { ...getCustomMeteres({ productPlan }) },
      },
    },
  }
}

export const getCustomMeteres = ({
  productPlan,
  pricesValue,
}: {
  productPlan: ProductPlan
  pricesValue?: AzureRecurrentPriceField[]
}) => {
  let customMetersObject = {}

  const productsMeters = (productPlan?.dimensions as AzurePlanDimension)?.meters
  productsMeters?.forEach(
    ({ cloudDimensionId, includedQuantities, ...remaingProductsMeterData }) => {
      customMetersObject = {
        ...customMetersObject,
        [cloudDimensionId]: {
          ...remaingProductsMeterData,
          includedQuantities: pricesValue?.length
            ? includedQuantities?.filter(
                item =>
                  item?.billingTerm?.type ===
                    pricesValue[0]?.billingTerm?.type &&
                  item?.billingTerm?.value ===
                    pricesValue[0]?.billingTerm?.value
              )
            : includedQuantities,
        },
      }
    }
  )
  return customMetersObject
}

export const getBillingTermsList = (billingTermItem: BillingTerm) => {
  return {
    label: `${billingTermItem?.value}${' '}${billingTermItem?.type}`,
    value: `${billingTermItem?.value}${' '}${billingTermItem?.type}`,
    type: billingTermItem?.type,
    termValue: billingTermItem?.value,
  }
}
